import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRight, Package, Truck, Sparkles, RefreshCw } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { categoriesAPI, productsAPI } from '../services/api';
import MegaMenuPanel from './MegaMenuPanel';

const priceRanges = [
  { id: 1, label: 'Sub 100 Lei', min: 0, max: 100 },
  { id: 2, label: '100 - 500 Lei', min: 100, max: 500 },
  { id: 3, label: '500 - 1000 Lei', min: 500, max: 1000 },
  { id: 4, label: '1000 - 2500 Lei', min: 1000, max: 2500 },
  { id: 5, label: 'Peste 2500 Lei', min: 2500, max: null },
];

const CatalogSidebar = ({ 
  selectedBrands, 
  onBrandToggle, 
  selectedPriceRange, 
  onPriceRangeChange, 
  onResetFilters,
  selectedAvailability,
  onAvailabilityChange,
  selectedGrades = [],
  onGradeToggle,
  selectedStorages = [],
  onStorageToggle,
  compatData = { brands: [], models: [] },
  compatBrand = '',
  onCompatBrandChange,
  selectedCompatModels = [],
  onCompatModelToggle,
  attrFilters = [],
  selectedAttrs = {},
  onAttrToggle,
}) => {
  const [modelSearch, setModelSearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [grades, setGrades] = useState([]);
  const [storages, setStorages] = useState([]);
  const [availabilityStats, setAvailabilityStats] = useState({});
  const [specifications, setSpecifications] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [hoveredCat, setHoveredCat] = useState(null);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
  const menuCardRef = useRef(null);
  const hoverLeaveRef = useRef(null);
  
  const categorySlug = searchParams.get('category');

  const loadCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadFilters = async () => {
    setLoadingFilters(true);
    try {
      // Load brands for current category
      const brandsRes = await productsAPI.getBrands(categorySlug);
      setBrands(brandsRes.data || []);
      // Load aesthetic grades (Pre-Owned) — shown only when they exist
      try {
        const gradesRes = await productsAPI.getGrades(categorySlug);
        setGrades(gradesRes.data || []);
      } catch (e) {
        setGrades([]);
      }
      try {
        const storageRes = await productsAPI.getStorages(categorySlug);
        setStorages(storageRes.data || []);
      } catch (e) {
        setStorages([]);
      }
      
      // Load availability stats
      const availRes = await productsAPI.getAvailabilityStats(categorySlug);
      setAvailabilityStats(availRes.data || {});
      
      // Load specifications (if any)
      const specsRes = await productsAPI.getSpecifications(categorySlug, 5);
      setSpecifications(specsRes.data || []);
    } catch (error) {
      console.error('Error loading filters:', error);
    } finally {
      setLoadingFilters(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadFilters();
  }, [categorySlug]);

  // Get main categories (no parentId), sorted by admin-defined order
  const byOrder = (a, b) => (a.order || 0) - (b.order || 0) || (a.name || '').localeCompare(b.name || '');
  const mainCategories = categories.filter(cat => !cat.parentId).sort(byOrder);

  // Build full tree context from URL: ancestors chain + current + children/siblings
  let currentCategory = null;
  let ancestors = [];
  let childCategories = [];
  let siblingCategories = [];
  let title = 'Toate Categoriile';

  if (categorySlug && categories.length > 0) {
    const selectedCat = categories.find(cat => cat.slug === categorySlug);
    if (selectedCat) {
      currentCategory = selectedCat;
      title = selectedCat.name;
      let p = selectedCat.parentId ? categories.find(c => c._id === selectedCat.parentId) : null;
      while (p) {
        ancestors.unshift(p);
        p = p.parentId ? categories.find(c => c._id === p.parentId) : null;
      }
      childCategories = categories.filter(c => c.parentId === selectedCat._id).sort(byOrder);
      if (childCategories.length === 0 && selectedCat.parentId) {
        siblingCategories = categories
          .filter(c => c.parentId === selectedCat.parentId && c._id !== selectedCat._id)
          .sort(byOrder);
      }
    }
  }

  const availabilityFilters = [
    { id: 'inStock', label: 'În Stoc', icon: Package, count: availabilityStats.inStock || 0 },
    { id: 'supplierStock', label: 'Stoc Furnizor', icon: Truck, count: availabilityStats.supplierStock || 0 },
    { id: 'isNew', label: 'Noutăți', icon: Sparkles, count: availabilityStats.isNew || 0 },
    { id: 'isRefurbished', label: 'Resigilate', icon: RefreshCw, count: availabilityStats.isRefurbished || 0 },
    { id: 'outOfStock', label: 'Stoc Epuizat', icon: Package, count: availabilityStats.outOfStock || 0, isOutOfStock: true },
  ];

  const hoverEnter = (e, cat) => {
    if (hoverLeaveRef.current) { clearTimeout(hoverLeaveRef.current); hoverLeaveRef.current = null; }
    const rect = e.currentTarget.getBoundingClientRect();
    const cardRect = menuCardRef.current ? menuCardRef.current.getBoundingClientRect() : rect;
    setPanelPos({
      anchor: { top: rect.top, bottom: rect.bottom, right: rect.right },
      cardTop: cardRect.top
    });
    setHoveredCat(cat);
  };
  const hoverLeave = () => {
    hoverLeaveRef.current = setTimeout(() => setHoveredCat(null), 350);
  };
  const panelEnter = () => {
    if (hoverLeaveRef.current) { clearTimeout(hoverLeaveRef.current); hoverLeaveRef.current = null; }
  };

  return (
    <aside className="w-64 flex-shrink-0 sticky top-24 self-start">
      {/* Categories Card */}
      <Card ref={menuCardRef} className="rounded-2xl border-2 border-gray-100 overflow-hidden mb-4">
        <div className="bg-green-600 text-white p-4 font-bold text-lg">
          {title}
        </div>
        <nav className="divide-y divide-gray-100" data-testid="sidebar-category-tree">
          {!currentCategory ? (
            /* Root view: all main categories with hover flyout */
            mainCategories.map((cat) => {
              const hasSubcategories = categories.some(subcat => subcat.parentId === cat._id);
              return (
                <div
                  key={cat._id}
                  onMouseEnter={hasSubcategories ? (e) => hoverEnter(e, cat) : undefined}
                  onMouseLeave={hasSubcategories ? hoverLeave : undefined}
                >
                  <Link
                    to={`/catalog?category=${cat.slug}`}
                    className="flex items-center justify-between p-4 hover:bg-green-50 transition-colors group"
                    data-testid={`sidebar-cat-${cat.slug}`}
                  >
                    <span className="text-sm font-medium text-gray-700 group-hover:text-green-600">
                      {cat.name}
                    </span>
                    {hasSubcategories && (
                      <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-green-600" />
                    )}
                  </Link>
                </div>
              );
            })
          ) : (
            /* Tree view: ancestors chain > current > children/siblings */
            <>
              <Link
                to="/catalog"
                className="flex items-center p-3 hover:bg-green-50 transition-colors group"
                data-testid="sidebar-back-root"
              >
                <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-green-600 rotate-180 mr-2" />
                <span className="text-sm font-medium text-gray-700 group-hover:text-green-600">
                  Toate categoriile
                </span>
              </Link>

              {ancestors.map((anc, idx) => (
                <Link
                  key={anc._id}
                  to={`/catalog?category=${anc.slug}`}
                  className="flex items-center p-3 hover:bg-green-50 transition-colors group"
                  style={{ paddingLeft: `${16 + idx * 14}px` }}
                  data-testid={`sidebar-ancestor-${anc.slug}`}
                >
                  <ChevronRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-green-600 rotate-180 mr-2 flex-shrink-0" />
                  <span className="text-sm text-gray-600 group-hover:text-green-600 truncate">
                    {anc.name}
                  </span>
                </Link>
              ))}

              <div
                className="p-3 bg-green-50"
                style={{ paddingLeft: `${16 + ancestors.length * 14}px` }}
                data-testid="sidebar-current-category"
              >
                <span className="text-sm font-bold text-green-700">{currentCategory.name}</span>
              </div>

              {(childCategories.length > 0 ? childCategories : siblingCategories).map((cat) => {
                const hasSub = categories.some(sc => sc.parentId === cat._id);
                const indent = childCategories.length > 0 ? ancestors.length + 1 : ancestors.length;
                return (
                  <Link
                    key={cat._id}
                    to={`/catalog?category=${cat.slug}`}
                    className="flex items-center justify-between p-3 hover:bg-green-50 transition-colors group"
                    style={{ paddingLeft: `${16 + indent * 14}px` }}
                    data-testid={`sidebar-cat-${cat.slug}`}
                  >
                    <span className="text-sm text-gray-700 group-hover:text-green-600 truncate">
                      {cat.name}
                    </span>
                    {hasSub && (
                      <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-green-600 flex-shrink-0" />
                    )}
                  </Link>
                );
              })}
            </>
          )}
        </nav>
      </Card>

      {hoveredCat && !currentCategory && (
        <MegaMenuPanel
          category={hoveredCat}
          categories={categories}
          anchor={panelPos.anchor}
          cardTop={panelPos.cardTop}
          onEnter={panelEnter}
          onLeave={() => setHoveredCat(null)}
        />
      )}

      {/* Filters Card */}
      <Card className="p-6 rounded-2xl border-2 border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-bold text-lg">Filtre</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onResetFilters}
            data-testid="sidebar-reset-filters"
            className="text-sm text-green-600 hover:text-green-700 rounded-xl"
          >
            Resetează
          </Button>
        </div>

        {/* Availability Filters - NEW */}
        <div className="mb-6">
          <h3 className="font-semibold mb-3">Disponibilitate</h3>
          <div className="space-y-2">
            {availabilityFilters.map((filter) => {
              const Icon = filter.icon;
              return (
                <div key={filter.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`avail-${filter.id}`}
                    checked={selectedAvailability?.[filter.id] || false}
                    onCheckedChange={(checked) => {
                      onAvailabilityChange?.(filter.id, checked);
                    }}
                    className="rounded-md"
                  />
                  <label
                    htmlFor={`avail-${filter.id}`}
                    className="text-sm cursor-pointer flex items-center gap-2"
                  >
                    <Icon className="h-4 w-4 text-gray-500" />
                    {filter.label}
                    {filter.count > 0 && (
                      <span className="text-xs text-gray-400">({filter.count})</span>
                    )}
                  </label>
                </div>
              );
            })}
          </div>
        </div>

        {/* Price Range */}
        <div className="mb-6">
          <h3 className="font-semibold mb-3">Preț</h3>
          <div className="space-y-2">
            {priceRanges.map((range) => (
              <div key={range.id} className="flex items-center space-x-2">
                <Checkbox
                  id={`price-${range.id}`}
                  checked={selectedPriceRange?.id === range.id}
                  onCheckedChange={(checked) => {
                    onPriceRangeChange(checked ? range : null);
                  }}
                  className="rounded-md"
                />
                <label
                  htmlFor={`price-${range.id}`}
                  className="text-sm cursor-pointer"
                >
                  {range.label}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Dynamic Brands from Database */}
        <div className="mb-6">
          <h3 className="font-semibold mb-3">Brand</h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {loadingFilters ? (
              <div className="text-sm text-gray-500">Se încarcă...</div>
            ) : brands.length > 0 ? (
              brands.map((brand) => (
                <div key={brand.name} className="flex items-center space-x-2">
                  <Checkbox
                    id={`brand-${brand.name}`}
                    checked={selectedBrands.includes(brand.name)}
                    onCheckedChange={() => onBrandToggle(brand.name)}
                    className="rounded-md"
                  />
                  <label
                    htmlFor={`brand-${brand.name}`}
                    className="text-sm cursor-pointer flex items-center justify-between w-full"
                  >
                    <span>{brand.name}</span>
                    <span className="text-xs text-gray-400">({brand.count})</span>
                  </label>
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-500">Niciun brand disponibil</div>
            )}
          </div>
        </div>

        {/* Aesthetic grade (Pre-Owned phones) */}
        {grades.length > 0 && (
          <div className="mb-6" data-testid="grade-filter-group">
            <h3 className="font-semibold mb-3">Stare estetică (Pre-Owned)</h3>
            <div className="space-y-2">
              {grades.map((g) => (
                <div key={g.grade} className="flex items-center space-x-2">
                  <Checkbox
                    id={`grade-${g.grade}`}
                    checked={selectedGrades.includes(g.grade)}
                    onCheckedChange={() => onGradeToggle && onGradeToggle(g.grade)}
                    className="rounded-md"
                    data-testid={`grade-checkbox-${g.grade}`}
                  />
                  <label
                    htmlFor={`grade-${g.grade}`}
                    className="text-sm cursor-pointer flex items-center justify-between w-full"
                  >
                    <span>Grad {g.grade}</span>
                    <span className="text-xs text-gray-400">({g.count})</span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Capacitate stocare (Pre-Owned phones) */}
        {storages.length > 0 && (
          <div className="mb-6" data-testid="storage-filter-group">
            <h3 className="font-semibold mb-3">Capacitate</h3>
            <div className="flex flex-wrap gap-2">
              {storages.map((s) => {
                const on = selectedStorages.includes(s.storage);
                return (
                  <button key={s.storage} type="button" onClick={() => onStorageToggle && onStorageToggle(s.storage)}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                      on ? 'border-green-600 bg-green-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-green-600'}`}
                    data-testid={`storage-chip-${s.storage}`}>
                    {s.storage} <span className={on ? 'text-green-100' : 'text-gray-400'}>({s.count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Compatibility (huse, folii, accesorii) */}
        {compatData.brands.length > 0 && (
          <div className="mb-6" data-testid="compat-filter-group">
            <h3 className="font-semibold mb-3">Compatibilitate</h3>
            <div className="space-y-2 mb-3">
              {compatData.brands.map((b) => (
                <div key={b.name} className="flex items-center space-x-2">
                  <Checkbox
                    id={`compat-brand-${b.name}`}
                    checked={compatBrand === b.name}
                    onCheckedChange={(checked) => onCompatBrandChange && onCompatBrandChange(checked ? b.name : '')}
                    className="rounded-md"
                    data-testid={`compat-brand-${b.name}`}
                  />
                  <label
                    htmlFor={`compat-brand-${b.name}`}
                    className="text-sm cursor-pointer flex items-center justify-between w-full"
                  >
                    <span>{b.name}</span>
                    <span className="text-xs text-gray-400">({b.count})</span>
                  </label>
                </div>
              ))}
            </div>
            {compatBrand && compatData.models.length > 0 && (
              <div className="border-t pt-3">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Model compatibil</h4>
                <input
                  type="text"
                  value={modelSearch}
                  onChange={(e) => setModelSearch(e.target.value)}
                  placeholder="Caută model..."
                  className="w-full mb-2 px-3 py-1.5 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-green-500"
                  data-testid="compat-model-search"
                />
                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {compatData.models
                    .filter((m) => !modelSearch || m.name.toLowerCase().includes(modelSearch.toLowerCase()))
                    .map((m) => (
                      <div key={m.name} className="flex items-center space-x-2">
                        <Checkbox
                          id={`compat-model-${m.name}`}
                          checked={selectedCompatModels.includes(m.name)}
                          onCheckedChange={() => onCompatModelToggle && onCompatModelToggle(m.name)}
                          className="rounded-md"
                          data-testid={`compat-model-${m.name}`}
                        />
                        <label
                          htmlFor={`compat-model-${m.name}`}
                          className="text-sm cursor-pointer flex items-center justify-between w-full"
                        >
                          <span className="truncate">{m.name}</span>
                          <span className="text-xs text-gray-400 flex-shrink-0">({m.count})</span>
                        </label>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Filtre dinamice pe atribute (scheme aprobate per categorie) */}
        {attrFilters.length > 0 && (
          <div className="mb-6" data-testid="attr-filters-section">
            {attrFilters.map((f) => (
              <div key={f.key} className="mb-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">{f.label}</h4>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {f.values.map((v) => (
                    <div key={v.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`attr-${f.key}-${v.value}`}
                        checked={selectedAttrs[f.key] === v.value}
                        onCheckedChange={() => onAttrToggle && onAttrToggle(f.key, v.value)}
                        data-testid={`attr-filter-${f.key}-${v.value}`}
                      />
                      <label htmlFor={`attr-${f.key}-${v.value}`} className="text-sm cursor-pointer flex items-center justify-between w-full">
                        <span className="truncate">{v.value}</span>
                        <span className="text-xs text-gray-400 shrink-0">({v.count})</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Dynamic Specifications */}
        {attrFilters.length === 0 && specifications.length > 0 && (
          <div className="mb-6">
            <h3 className="font-semibold mb-3">Specificații</h3>
            {specifications.slice(0, 3).map((spec) => (
              <div key={spec.key} className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">{spec.key}</h4>
                <div className="space-y-1">
                  {spec.values.slice(0, 5).map((val) => (
                    <div key={val.value} className="text-xs text-gray-500 flex justify-between">
                      <span>{val.value}</span>
                      <span>({val.count})</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </aside>
  );
};

export default CatalogSidebar;
